/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { TranslationCreationSteps, TranslationInfoDto } from 'src/translation/components/TranslationCreationPage';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';

export type TranslationReviewAndFinishPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    translationConfigsDto: TranslationInfoDto;
    activeStep: TranslationCreationSteps;
    isReturningUser: boolean;
    isLoading: boolean;
};

const messages = defineMessages({
    pageTitle: { id: 'translation.translationReviewAndFinishPage.pageTitle', defaultMessage: 'Review and finish' },
    pageSubtitle: {
        id: 'translation.translationReviewAndFinishPage.pageSubtitle',
        defaultMessage: 'Review your selections before starting your translation job'
    },
    sourceLanguageSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.sourceLanguageSectionTitle',
        defaultMessage: 'Source language'
    },
    targetLanguageSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.targetLanguageSectionTitle',
        defaultMessage: 'Target language(s)'
    },
    filesNumberSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.filesNumberSectionTitle',
        defaultMessage: 'Number of files'
    },
    filesSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.filesSectionTitle',
        defaultMessage: 'Files'
    },
    fileOriginSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.fileOriginSectionTitle',
        defaultMessage: 'File origin'
    },
    fileDestinationSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.fileDestinationSectionTitle',
        defaultMessage: 'File destination'
    },
    modelCategoryIdSectionTitle: {
        id: 'translation.translationReviewAndFinishPage.modelCategoryIdSectionTitle',
        defaultMessage: 'Model category ID'
    },
    noFilesSelected: {
        id: 'translation.translationReviewAndFinishPage.noFilesSelected',
        defaultMessage: 'No files selected'
    },
    blobStorageTitle: {
        id: 'translation.translationReviewAndFinishPage.blobStorageTitle',
        defaultMessage: 'Blob Storage'
    },
    folderTitle: {
        id: 'translation.translationReviewAndFinishPage.folderTitle',
        defaultMessage: 'Container folder'
    },
    downloadFilesLocally: {
        id: 'translation.translationReviewAndFinishPage.downloadFilesLocally',
        defaultMessage: 'Download files locally'
    },
    noneTitle: {
        id: 'translation.translationReviewAndFinishPage.noneTitle',
        defaultMessage: 'None'
    }
});

const containerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { paddingLeft: '32px', paddingRight: '32px', overflow: 'auto', marginBottom: '10px' }
});

export const TranslationReviewAndFinishPage = observer((props: TranslationReviewAndFinishPageProps) => {
    const { onSubmit, onDismiss, onPrevious, translationConfigsDto, activeStep, isReturningUser, isLoading } = props;
    const { localize } = useLocalization();

    return (
        <Stack verticalFill verticalAlign="space-between" styles={{ root: { overflow: 'auto' } }}>
            <Stack tokens={{ childrenGap: 30 }} styles={containerStyles}>
                <Stack tokens={{ childrenGap: 13 }}>
                    <LuisText variant="xLarge" fontWeight={600}>
                        {localize(messages.pageTitle)}
                    </LuisText>
                    <LuisText variant="medium">{localize(messages.pageSubtitle)}</LuisText>
                </Stack>
                <Stack tokens={{ childrenGap: 20 }}>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.sourceLanguageSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">{translationConfigsDto.sourceLanguage.value}</LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.targetLanguageSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">{translationConfigsDto.targetLanguages.map(l => l.value).join(', ')}</LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.filesNumberSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">{translationConfigsDto.filesSelected.length}</LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.filesSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">
                            {translationConfigsDto.filesSelected.length
                                ? translationConfigsDto.filesSelected.map(f => f.name).join(', ')
                                : localize(messages.noFilesSelected)}
                        </LuisText>
                    </Stack>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.fileDestinationSectionTitle)}
                        </LuisText>

                        {translationConfigsDto.isUploadToBlobStore && (
                            <Stack>
                                <LuisText variant="medium">
                                    {localize(messages.blobStorageTitle)}:&nbsp;{translationConfigsDto.destinationContainerName}
                                </LuisText>
                                {translationConfigsDto.destinationFolder && (
                                    <LuisText variant="medium">
                                        {localize(messages.folderTitle)}:&nbsp;{translationConfigsDto.destinationFolder}
                                    </LuisText>
                                )}
                            </Stack>
                        )}
                        {translationConfigsDto.isDownloadToLocalStorage && (
                            <LuisText variant="medium">{localize(messages.downloadFilesLocally)}</LuisText>
                        )}
                    </Stack>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.modelCategoryIdSectionTitle)}
                        </LuisText>
                        <LuisText variant="medium">
                            {translationConfigsDto.customTranslatorModelId
                                ? translationConfigsDto.customTranslatorModelId
                                : localize(messages.noneTitle)}
                        </LuisText>
                    </Stack>
                </Stack>
            </Stack>
            <TranslationToolFooter
                activeStep={activeStep}
                isNextDisabled
                isReturningUser={isReturningUser}
                onDismiss={onDismiss}
                onNext={null}
                onPrevious={onPrevious}
                onSubmit={onSubmit}
                isLoading={isLoading}
            />
        </Stack>
    );
});
